/* eslint-disable comma-spacing */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import { HashRouter } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { Provider as ReduxProvider } from 'react-redux';
import createCache, { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import './index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { createAppTheme } from './AppTheme';
import { GTMService, IntlService, MessagesType } from './services';
import { store } from './store';
import { AppErrorBoundary } from './AppErrorBoundary';
import environment from './environments/environment';

const getRootElement = (): [EmotionCache, HTMLDivElement, ShadowRoot | undefined] => {
  const container = document.getElementById('root');
  const shadowContainer = container?.attachShadow({ mode: 'open' });
  const emotionRoot = document.createElement('style');
  const shadowRootElement = document.createElement('div');
  shadowContainer?.appendChild(emotionRoot);
  shadowContainer?.appendChild(shadowRootElement);

  const cache = createCache({
    key: 'css',
    prepend: true,
    container: emotionRoot,
  });

  return [cache, shadowRootElement, shadowContainer];
};

const bootstrapApp = (withMessages: MessagesType) => {
  const [cache, rootElement, shadowContainer] = getRootElement();

  GTMService.initialiseGTM(window, shadowContainer, environment.gtmId);

  // Create a root.
  const root = ReactDOM.createRoot(rootElement as HTMLElement);

  root.render(
    <React.StrictMode>
      <CacheProvider value={cache}>
        <ReduxProvider store={store}>
          <ThemeProvider theme={createAppTheme(rootElement)}>
            <ScopedCssBaseline>
              <HashRouter>
                <IntlProvider
                  locale={IntlService.UserLocale}
                  messages={withMessages}
                  defaultLocale={IntlService.DefaultLocale}
                  fallbackOnEmptyString={false}
                >
                  <AppErrorBoundary>
                    <App />
                  </AppErrorBoundary>
                </IntlProvider>
              </HashRouter>
            </ScopedCssBaseline>
          </ThemeProvider>
        </ReduxProvider>
      </CacheProvider>
    </React.StrictMode>,
  );
};

IntlService.LoadLanguage(IntlService.UserLocale).then((x) => bootstrapApp(x));

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
